/*
 * WELLF — Real-user Core Web Vitals + exit context  (ADDITIVE, isolated, site-wide)
 * ---------------------------------------------------------------------------
 * Sends ONE event per pageview, at the moment the visitor leaves:
 *   GA4: gtag('event', 'wellf_vitals', {...})
 *
 * WHY THIS EXISTS
 * Lab tools cannot answer the question we actually have. On 2026-08-27 a Lighthouse
 * run reported LCP 9.0s on the PDP while the same run OBSERVED LCP at 1.60s, and an
 * independently throttled run measured FCP at 0.5-0.9s. The headline was a Lantern
 * simulation artefact. CrUX has no data for this origin either (we are far below its
 * traffic threshold), so there is no field data to check lab runs against. The only
 * way to know what real visitors experience is to measure real visitors.
 *
 * The question it is built to answer: when a paid-social session ends in under ~2.3s,
 * was it a slow first paint, or an accidental tap? Those look identical in GA4 today.
 * With this event they separate cleanly:
 *   painted = 'no'                       -> they left before the page painted (SLOW)
 *   painted = 'yes', scroll 0, no input  -> accidental tap / wrong audience
 *   painted = 'yes', scrolled, still out -> message or offer problem, not speed
 *
 * GA4 ONLY — deliberately not sent to Meta. Vitals have no advertising use, and every
 * Meta custom event permanently enlarges that account's event dictionary.
 *
 * SAFETY / ISOLATION
 *  - Site-wide, but ONLY when settings.enable_wellf_vitals is on (pure kill-switch).
 *  - Passive listeners and PerformanceObservers only. Mutates no shared state, adds
 *    no UI, touches no DOM. A missing API is a no-op, never an error.
 *  - Consent-gated on the same rule as wellf-events.js: marketing AND analytics.
 *
 * CONSENT API — READ BEFORE TOUCHING consentOK()
 * Do NOT assume window.Shopify.customerPrivacy is put there for us. From 2026-08-24
 * Shopify's privacy banner stopped installing the real Customer Privacy API and wrote
 * only a method-less {cachedConsent, consentStatus} stub, which silently killed every
 * wellf_* event for three days. We request the API explicitly below. consentOK() fails
 * closed if it cannot load, and that is deliberate.
 *
 * READING THE DATA — three traps, all learned from the first day of real traffic:
 *   - Always COUNT(DISTINCT vid), never COUNT(*). GA4's transport retries in the
 *     Instagram in-app browser and delivers the same pageview twice.
 *   - Exclude bg_start='yes' from any paint metric. Those pages loaded into a
 *     background tab, so LCP measures when someone looked, not how fast we were.
 *   - painted='bfcache' is NOT a slow page. No paint entry fires on a bfcache restore.
 *     Only painted='no' means the visitor left before anything rendered.
 *
 * KNOWN AND ACCEPTED SAMPLING BIAS
 * A visitor who bounces before answering the consent banner sends nothing at all, so
 * the very fastest bounces are under-sampled. This is not fixable without tracking
 * people who have not consented. It is also not a new distortion: GA4 itself only sees
 * consenting sessions, so this event covers exactly the same population as the bounce
 * figures it will be compared against. Read it as "of the sessions GA4 can see", never
 * as "of all visitors".
 */
(function () {
  'use strict';

  var CS = document.currentScript;
  var DEBUG = !!(CS && CS.getAttribute('data-debug') === 'true');
  var PAGE = (CS && CS.getAttribute('data-page')) || 'other';

  if (!('PerformanceObserver' in window)) return;

  // ── ask Shopify for the real Customer Privacy API (see note up top) ──
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }]);
    }
  } catch (e) {}

  function consentOK() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || !cp.marketingAllowed || !cp.analyticsProcessingAllowed) return false;
      return cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  function now() { return (window.performance && performance.now) ? performance.now() : Date.now(); }

  // Wrap every observer: an unsupported entry type throws, and must never break the page.
  function observe(type, cb, extra) {
    try {
      var opts = { type: type, buffered: true };
      if (extra) for (var k in extra) opts[k] = extra[k];
      var po = new PerformanceObserver(function (list) { try { cb(list, po); } catch (e) {} });
      po.observe(opts);
      return po;
    } catch (e) { return null; }
  }

  // ── navigation timing: TTFB + how they arrived ──
  var ttfb = null, navType = null;
  try {
    var nav = performance.getEntriesByType('navigation')[0];
    if (nav) {
      ttfb = Math.round(nav.responseStart);
      navType = nav.type;                       // navigate | reload | back_forward | prerender
    }
  } catch (e) {}

  // ── FCP ──
  var fcp = null;
  observe('paint', function (list) {
    list.getEntries().forEach(function (e) {
      if (e.name === 'first-contentful-paint' && fcp === null) fcp = Math.round(e.startTime);
    });
  });

  // ── LCP. Keep the LATEST candidate; it is final once the visitor interacts or leaves. ──
  var lcp = null, lcpEl = null;
  function describe(el) {
    try {
      if (!el) return null;
      var cls = (typeof el.className === 'string' && el.className) ? '.' + el.className.trim().split(/\s+/)[0] : '';
      return (el.tagName.toLowerCase() + cls).slice(0, 40);
    } catch (e) { return null; }
  }
  var lcpObs = observe('largest-contentful-paint', function (list) {
    var es = list.getEntries(), last = es[es.length - 1];
    if (last) { lcp = Math.round(last.startTime); lcpEl = describe(last.element); }
  });

  // ── CLS, proper session-window algorithm (max 5s window, 1s gap) ──
  var cls = 0, winVal = 0, winFirst = 0, winLast = 0;
  observe('layout-shift', function (list) {
    list.getEntries().forEach(function (e) {
      if (e.hadRecentInput) return;
      if (winVal && e.startTime - winLast < 1000 && e.startTime - winFirst < 5000) {
        winVal += e.value; winLast = e.startTime;
      } else {
        winVal = e.value; winFirst = winLast = e.startTime;
      }
      if (winVal > cls) cls = winVal;
    });
  });

  // ── First CONTENT interaction: the accidental-tap discriminator.
  // BUG FOUND IN TESTING (2026-08-27): the browser's own 'first-input' entry is the
  // visitor clicking ACCEPT on the cookie banner. Since we only ever report consenting
  // sessions, that made interacted='yes' for literally everyone and destroyed the whole
  // point of the field. So we detect first input ourselves and ignore anything inside
  // Shopify's consent UI (#shopify-pc__banner / #shopify-pc__prefs). ──
  var firstInput = null;
  function isConsentUI(el) {
    try { return !!(el && el.closest && el.closest('[id^="shopify-pc"]')); } catch (e) { return false; }
  }
  function noteFirstInput(ev) {
    if (firstInput !== null) return;
    if (isConsentUI(ev.target)) return;
    firstInput = Math.round(now());
  }
  document.addEventListener('pointerdown', noteFirstInput, { capture: true, passive: true });
  document.addEventListener('keydown', noteFirstInput, { capture: true, passive: true });

  // ── INP. We take the worst interaction latency. For sessions with fewer than 50
  // interactions INP is DEFINED as the worst interaction, and a PDP session is nowhere
  // near 50, so on this site this is INP exactly, not an approximation.
  // Interactions before the first CONTENT interaction are the consent banner, and are
  // ignored for the same reason as above: they measure Shopify's UI, not our page. ──
  var inp = 0;
  observe('event', function (list) {
    list.getEntries().forEach(function (e) {
      if (!e.interactionId) return;
      if (firstInput === null || e.startTime < firstInput - 50) return;
      var d = Math.round(e.duration);
      if (d > inp) inp = d;
    });
  }, { durationThreshold: 16 });

  // ── engagement context: how far and how long, so bounce can be classified ──
  var maxScroll = 0, scrollTick = 0;
  function readScroll() {
    try {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      var pct = scrollable <= 0 ? 100 : Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      if (pct > maxScroll) maxScroll = pct;
    } catch (e) {}
  }
  window.addEventListener('scroll', function () {
    var t = now();
    if (t - scrollTick < 200) return;
    scrollTick = t; readScroll();
  }, { passive: true });
  readScroll();
  // Scroll position AT EXIT, which is a different question from max_scroll_pct:
  // "where were they when they left" vs "how far did they ever get".
  function maxScrollNow() {
    try {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return 100;
      return Math.min(100, Math.round((window.scrollY / scrollable) * 100));
    } catch (e) { return maxScroll; }
  }

  // Seed with the time already elapsed since navigation. This script is deferred, so it
  // starts a few hundred ms in — material when the thing we are measuring is a 2.3s
  // bounce. Slightly over-counts if the tab was backgrounded before we loaded, which is
  // rare and errs in the harmless direction.
  var visibleMs = (document.visibilityState !== 'hidden') ? now() : 0;
  var lastTick = now(), visible = (document.visibilityState !== 'hidden');
  function accrue() { var t = now(); if (visible) visibleMs += t - lastTick; lastTick = t; }

  // ── Was this pageview loaded into a BACKGROUND tab? If so its paint timings are
  // meaningless: nothing renders until the tab is focused, so LCP and FCP measure when
  // the person looked at it, not how fast we were. First day of real data produced one
  // of these at 89,524 ms with FCP and LCP identical, which is the signature. Flagged
  // rather than dropped, so it can be excluded in SQL without silently losing rows. ──
  var bgStart = (document.visibilityState === 'hidden');

  // ── Was this a bfcache restore? No LCP or FCP entry fires on one, so the paint
  // fields are absent BY DESIGN, not because the page was slow. First day of data had
  // two of these reported as painted='no' with an 11 ms TTFB, which read as "visitor
  // left before the page painted" when nothing of the sort happened. ──
  var restored = false;
  window.addEventListener('pageshow', function (ev) {
    try { if (ev && ev.persisted) restored = true; } catch (e) {}
  }, { passive: true });

  // ── One id per pageview, so duplicate deliveries can be collapsed in SQL.
  // GA4's transport retries in the Instagram in-app browser (Android WebView): the first
  // day produced three duplicate pairs, same page instance and same dwell to the
  // millisecond, 30-106 ms apart with different bundle ids. Our in-page guard cannot see
  // that, because it happens below us. COUNT(DISTINCT vid) is the honest pageview count. ──
  var vid = '';
  try {
    vid = Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
  } catch (e) { vid = ''; }

  // =========================================================================
  // ZONE DWELL VECTOR  (item B, 2026-08-31)  ·  wellf_zone_dwell
  // Where attention actually went, as ONE event per pageview rather than a stream of
  // per-zone events. This exists because the audit showed five separate "intent"
  // instruments were all proxies of a single variable, scroll depth: price_survivor was
  // Jaccard 0.92 with scroll_50. Orthogonal per-zone dwell replaces them. NO composite
  // score is computed here or anywhere - the vector IS the instrument.
  //
  // Qualification, as of 2026-09-01: exactly ONE zone accrues at a time, the one
  // overlapping the middle fifth of the viewport most. See CENTRE-BAND ATTRIBUTION below
  // for why, and for what the previous ">=50% visible" rule actually measured. Always
  // AND-ed with document visibility, on the same clock as dwell_ms. Re-entries need >=2s
  // out before they count again, so a wobble at a boundary cannot inflate them, and are
  // capped at 9. z_plancards_re >= 1 is the honest "came back to the price" signal that
  // retired price_return.
  // =========================================================================
  // 2026-09-01: a ninth zone, socialproof, joined the vector when the taste reel band
  // shipped. z_socialproof_* is the new band; z_reviews_* now ALSO covers the reel teaser,
  // because the teaser sits inside .prv and .prv carries the zone attribute. That is
  // defensible, it is all one social-proof block, but z_reviews_ms is not comparable with
  // its pre-1-Sep self.
  // The desktop column overlap that used to distort this (gallery left, title/plancards
  // right, all accruing together) is GONE: centre-band credits one zone at a time, and the
  // sticky gallery is excluded from the vector entirely. Both fixed below.
  var ZONE_REENTRY_CAP = 9;
  var ZONE_OUT_MS = 2000;
  var zones = [];
  try {
    // PDP ONLY. wellf-vitals.js is loaded site-wide because Core Web Vitals are a
    // site-wide question, but the zone vector is not: its names, thresholds and every
    // downstream reading of it are PDP intent semantics. Without this guard any shared
    // snippet carrying data-wellf-zone emits wellf_zone_dwell from whatever page it lands
    // on, and homepage rows get silently mixed into PDP zone analysis. The taste reel band
    // renders on BOTH templates and carries data-wellf-zone="socialproof", so this is not
    // hypothetical. zones stays empty elsewhere, which also suppresses the tick, the event
    // and exit_zone, since all three are guarded on zones.length.
    if (PAGE !== 'pdp') throw 0;
    Array.prototype.forEach.call(document.querySelectorAll('[data-wellf-zone]'), function (el) {
      var name = (el.getAttribute('data-wellf-zone') || '').trim();
      if (!name || !/^[a-z][a-z0-9_]{0,11}$/.test(name)) return;   // keep param names sane
      // A zone pinned with position:sticky never leaves the viewport, so any time-based
      // reading of it is an artefact of CSS, not of the visitor. .pdp__gallery is sticky at
      // >=780px and static below, so this is layout-aware on its own: z_gallery_ms exists
      // on mobile, where the gallery scrolls like everything else, and is absent on
      // desktop, where it would only ever have measured "the page was open". Gallery
      // engagement is carried by gallery_swipe, which needs a deliberate act.
      var stuck = false;
      try {
        for (var a = el; a && a !== document.body; a = a.parentElement) {
          if (getComputedStyle(a).position === 'sticky') { stuck = true; break; }
        }
      } catch (e) {}
      if (stuck) return;
      zones.push({ name: name, el: el, ms: 0, re: 0, inSince: -1, outSince: -1, qualified: false });
    });
  } catch (e) {}

  function zoneAccrue(z) {
    if (z.qualified && z.inSince >= 0) { z.ms += visibleMs - z.inSince; z.inSince = visibleMs; }
  }
  function zoneAccrueAll() { for (var i = 0; i < zones.length; i++) zoneAccrue(zones[i]); }

  // ── READ-ONLY SNAPSHOT OF THE ZONE COUNTERS ──
  // The £5 auto-open trigger in wellf-welcome.js decides on "sat on the price" and "came
  // back to the price", which are exactly z_plancards_ms and z_plancards_re. It reads them
  // from HERE rather than recomputing, so the thing that fires the modal and the thing we
  // later measure can never drift apart: same centre-band rule, same sticky exclusion,
  // same re-entry debounce.
  //
  // Two things make this safe to expose. It accrues first, because z.ms only advances when
  // zoneAccrue runs and a stale read would under-report the zone the visitor is sitting in
  // right now. And it returns a copy, so nothing outside this module can corrupt the
  // counters that feed the event.
  //
  // NOTE ON CONSENT: consentOK() gates the SEND, not this. The ticker accrues for every
  // visitor, so the trigger works on 100% of sessions including those who declined. Do not
  // "fix" that by moving the gate up here.
  window.WellfZones = {
    read: function () {
      try {
        zoneAccrueAll();
        var out = {};
        for (var i = 0; i < zones.length; i++) {
          out[zones[i].name] = { ms: Math.round(zones[i].ms), re: zones[i].re };
        }
        return out;
      } catch (e) { return {}; }
    }
  };

  // ── CENTRE-BAND ATTRIBUTION (2026-09-01) ──
  // Replaces ">=50% of the zone visible, or the zone covering >=50% of the viewport".
  // That rule credited EVERY qualifying zone at once, so it was not an attention vector,
  // it was a list of what was on screen. Measured on the old rule, an identical 6s park on
  // the plan cards: mobile credited mktcomp 6,733ms, guarantee 6,700 and plancards 6,670,
  // because an 844px viewport holds four ~200px zones simultaneously; desktop credited the
  // sticky gallery 12,907ms, more than any real zone.
  //
  // Now exactly one zone is credited per tick: the one containing the viewport's vertical
  // centre. People scroll so that what they are reading sits near the middle, so this is
  // the standard reading-position proxy. It is exclusive by construction, so the vector
  // sums to dwell time, and it is indifferent to how tall a zone is, which winner-by-area
  // is not. Ties break to the SMALLEST zone, so a specific block beats a container.
  var ZONE_TICK_MS = 250;
  var currentZone = null;

  // A BAND, not a line: the middle fifth of the viewport. A bare centre line falls into
  // the margins between sections and credits nothing, which measured as a 6s park on the
  // plan cards banking 0ms because the line landed in a 21px gap. The band is ~169px on a
  // 844px viewport, wider than any gutter on the page. Winner is the zone overlapping the
  // band most; ties still break to the smallest zone so a specific block beats a container.
  var ZONE_BAND = 0.10;                                 // +/- 10% of viewport height
  function centreZone() {
    try {
      var vh = window.innerHeight || document.documentElement.clientHeight || 1;
      var top = vh * (0.5 - ZONE_BAND), bot = vh * (0.5 + ZONE_BAND);
      var best = null, bestOverlap = 0, bestH = Infinity;
      for (var i = 0; i < zones.length; i++) {
        var r = zones[i].el.getBoundingClientRect();
        if (r.height <= 0) continue;
        var ov = Math.min(r.bottom, bot) - Math.max(r.top, top);
        if (ov <= 0) continue;
        if (ov > bestOverlap || (ov === bestOverlap && r.height < bestH)) {
          best = zones[i]; bestOverlap = ov; bestH = r.height;
        }
      }
      return best;
    } catch (e) { return null; }
  }

  // ── FREEZE THE ZONE CLOCK WHILE AN OVERLAY IS OPEN ──
  // The ticker had no modal awareness, and every modal locks body scroll, so time spent in
  // the guarantee, lab-report, perk or welcome modal was credited to whichever zone happened
  // to be under the centre band. The £5 auto-open made that systematic rather than
  // incidental: it fires on plancards dwell, and the modal it opens then inflates plancards
  // dwell, contaminating the number we would use to re-tune the threshold.
  //
  // Returning false on error keeps measuring rather than silently stopping.
  var zonesFrozen = false;
  function overlayOpenForZones() {
    try {
      if (window.WellfKlaviyo && typeof window.WellfKlaviyo.anyOverlayOpen === 'function') {
        return window.WellfKlaviyo.anyOverlayOpen();
      }
      if (document.querySelector('[data-wellf-modal]:not([hidden])')) return true;
      if (document.querySelector('dialog[open]')) return true;
      return !!(document.body && document.body.style.overflow === 'hidden');
    } catch (e) { return false; }
  }

  function zoneTick() {
    if (!visible) return;
    accrue();

    if (overlayOpenForZones()) {
      // Bank what has accrued and stop the clock. Deliberately does NOT touch outSince:
      // ZONE_OUT_MS is 2000, so any modal open for more than two seconds would otherwise
      // register a spurious RE-ENTRY on close and inflate z_*_re, which is the very signal
      // the £5 auto-open fires on. This is a freeze, not an exit.
      if (!zonesFrozen && currentZone) { zoneAccrue(currentZone); currentZone.inSince = -1; }
      zonesFrozen = true;
      return;
    }
    if (zonesFrozen) {
      zonesFrozen = false;
      // resume the same zone from now: no exit, no re-entry, no modal time credited
      if (currentZone) { currentZone.inSince = visibleMs; }
      return;
    }

    var z = centreZone();
    if (z === currentZone) return;
    if (currentZone) {
      zoneAccrue(currentZone);
      currentZone.inSince = -1; currentZone.outSince = visibleMs; currentZone.qualified = false;
    }
    if (z) {
      if (z.outSince >= 0 && (visibleMs - z.outSince) >= ZONE_OUT_MS && z.re < ZONE_REENTRY_CAP) z.re++;
      z.inSince = visibleMs; z.outSince = -1; z.qualified = true;
    }
    currentZone = z;
  }

  if (zones.length) {
    setInterval(zoneTick, ZONE_TICK_MS);
    zoneTick();
  }

  // =========================================================================
  // EXIT STATE  (item C, 2026-08-31)
  // Plan state is derived HERE from the DOM rather than read from wellf-events.js.
  // The two modules have independent kill-switches, so importing the state would make
  // these params silently null whenever enable_wellf_events was off. Deriving locally
  // costs one listener and cannot desync.
  // =========================================================================
  var planTouched = false, planToggles = 0, lastToggleAt = -1;
  function planCurrent() {
    try {
      var pressed = document.querySelector('[data-buy-mode][aria-pressed="true"]');
      return pressed ? pressed.getAttribute('data-buy-mode') : null;
    } catch (e) { return null; }
  }
  var planMode = planCurrent();
  Array.prototype.forEach.call(document.querySelectorAll('[data-buy-mode]'), function (btn) {
    btn.addEventListener('click', function () {
      var m = btn.getAttribute('data-buy-mode');
      if (m === planMode) return;                 // re-click of the selected plan is not a change
      planMode = m; planTouched = true; planToggles++;
      accrue(); lastToggleAt = visibleMs;
    }, { passive: true });
  });

  var lastInputAt = -1;
  function noteInput() { accrue(); lastInputAt = visibleMs; }
  document.addEventListener('pointerdown', noteInput, { capture: true, passive: true });
  document.addEventListener('keydown', noteInput, { capture: true, passive: true });

  // The zone under the viewport centre at exit. Was "greatest visible height", which on
  // desktop resolved to the sticky gallery or to whichever section happened to be tallest,
  // and returned a different answer from mobile for an identical session.
  function exitZoneName() {
    var z = currentZone || centreZone();
    return z ? z.name : 'none';
  }

  // =========================================================================
  // COMPARISON-LOOP SIGNALS  (item D, 2026-08-31)
  // Both of these are DELIBERATE acts that scrolling cannot produce, which is the
  // whole point: the audit showed the existing "intent" events were five proxies of
  // one variable (scroll depth). Copying a price and leaving to compare, or going away
  // and coming back, are orthogonal to depth.
  // Consent-gated like everything else, and a copy that happens pre-consent is DROPPED,
  // not queued - it is visitor behaviour, and the standing rule is never to replay it.
  // =========================================================================
  function emit(name, params) {
    if (!consentOK()) return false;
    try {
      if (typeof window.gtag !== 'function') return false;
      var p = params || {};
      p.page = PAGE;
      if (vid) p.vid = vid;
      window.gtag('event', name, p);
      if (DEBUG) { try { console.log('[wellf-vitals]', name, p); } catch (e) {} }
      return true;
    } catch (e) { return false; }
  }

  function norm(v) { return String(v == null ? '' : v).toLowerCase().replace(/\s+/g, ' ').trim(); }

  // The zone containing a node, walked up the tree. Used by copy and away-return.
  function zoneOfNode(node) {
    try {
      var el = node && (node.nodeType === 1 ? node : node.parentElement);
      while (el && el !== document.documentElement) {
        if (el.getAttribute && el.getAttribute('data-wellf-zone')) return el.getAttribute('data-wellf-zone');
        el = el.parentElement;
      }
    } catch (e) {}
    return 'other';
  }

  // ── wellf_copy · at most 2 per pageview. The copied TEXT IS NEVER SENT, only a
  // classification of it. Read the code before changing: p carries copy_kind, not the
  // string that produced it. ──
  var COPY_CAP = 2, copyCount = 0;
  var titleText = norm((document.querySelector('[data-wellf-zone="title"] h1, .close__buy-name, h1') || {}).textContent);
  var ingredientText = (function () {
    try {
      var items = document.querySelectorAll('.acc__item');
      for (var i = 0; i < items.length; i++) {
        var lab = norm((items[i].querySelector('.acc__q-text') || {}).textContent);
        if (lab.indexOf('ingredient') > -1 || lab.indexOf('nutrition') > -1) return norm(items[i].textContent);
      }
    } catch (e) {}
    return '';
  })();

  function classifyCopy(raw) {
    var t = norm(raw);
    if (!t) return 'other';
    if (/£\s?\d/.test(raw)) return 'price';
    if (titleText && t.length > 3 && (titleText.indexOf(t) > -1 || t.indexOf(titleText) > -1)) return 'title';
    if (ingredientText && t.length > 3 && ingredientText.indexOf(t) > -1) return 'ingredient';
    return 'other';
  }

  document.addEventListener('copy', function () {
    if (copyCount >= COPY_CAP) return;
    var sel = null, raw = '';
    try { sel = window.getSelection(); raw = String(sel || ''); } catch (e) {}
    if (!norm(raw)) return;
    var zone = 'other';
    try { zone = zoneOfNode(sel && sel.anchorNode); } catch (e) {}
    copyCount++;
    emit('wellf_copy', { copy_zone: zone, copy_kind: classifyCopy(raw), copy_index: copyCount });
  }, { passive: true });

  // ── wellf_away_return · tab hidden >=30s then back, on the same pageview.
  // away_ms is WALL time deliberately: the visitor really was gone that long, and the
  // visible-time clock by definition does not advance while hidden. ──
  var AWAY_MIN_MS = 30000, AWAY_CAP = 3, AWAY_MAX_MS = 1800000;
  var awayCount = 0, hiddenAt = -1;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { hiddenAt = now(); return; }
    if (hiddenAt < 0) return;
    var awayMs = now() - hiddenAt;
    hiddenAt = -1;
    if (awayMs < AWAY_MIN_MS || awayCount >= AWAY_CAP) return;
    awayCount++;
    emit('wellf_away_return', {
      away_ms: Math.min(Math.round(awayMs), AWAY_MAX_MS),
      away_index: awayCount,
      zone_at_return: exitZoneName()
    });
  });

  // ── delivery. Fire on the FIRST of hidden/pagehide, before the tab can freeze.
  // Mirrors the Engaged90s fix in wellf-events.js: never bind delivery to a tick that
  // may not run again. If consent is not granted yet we do NOT mark it sent, so a later
  // pagehide can still deliver it. ──
  var sent = false;
  function send() {
    if (sent) return;
    accrue();
    if (!consentOK()) return;
    if (lcpObs) { try { lcpObs.disconnect(); } catch (e) {} }

    var c = null;
    try { c = navigator.connection || null; } catch (e) {}

    // painted now also counts FCP, not just LCP: if anything rendered, the visitor saw
    // the page, and treating "LCP entry absent" as "never painted" overstated the
    // pre-paint exits. bfcache restores are their own value rather than a false 'no'.
    var paintedVal;
    if (restored) paintedVal = 'bfcache';
    else if (lcp !== null || fcp !== null) paintedVal = 'yes';
    else paintedVal = 'no';

    var p = {
      page: PAGE,
      painted: paintedVal,
      dwell_ms: Math.round(visibleMs),
      max_scroll_pct: maxScroll,
      interacted: firstInput === null ? 'no' : 'yes',
      cls: Math.round(cls * 1000) / 1000,
      nav_type: navType || 'unknown',
      bg_start: bgStart ? 'yes' : 'no',
      // Viewport width, so every geometry-dependent reading can be split by layout rather
      // than by GA4's device_category, which buckets a 1024px tablet with a phone. The PDP
      // goes two-column around 780px and the two layouts do not measure the same thing.
      vw: window.innerWidth || 0
    };
    if (vid) p.vid = vid;
    if (lcp !== null) p.lcp_ms = lcp;
    if (fcp !== null) p.fcp_ms = fcp;
    if (ttfb !== null) p.ttfb_ms = ttfb;
    if (inp > 0) p.inp_ms = inp;
    if (firstInput !== null) p.first_input_ms = firstInput;
    if (lcpEl) p.lcp_element = lcpEl;
    if (c) {
      if (c.effectiveType) p.conn = c.effectiveType;
      if (typeof c.downlink === 'number') p.downlink = c.downlink;
      if (c.saveData === true) p.save_data = 'yes';
    }
    try { if (navigator.deviceMemory) p.device_mem = navigator.deviceMemory; } catch (e) {}
    try { if (navigator.hardwareConcurrency) p.cpu_cores = navigator.hardwareConcurrency; } catch (e) {}

    try {
      if (typeof window.gtag === 'function') { window.gtag('event', 'wellf_vitals', p); sent = true; }
    } catch (e) {}

    // ── second event on the SAME unload path: the zone vector + exit state.
    // Kept separate from wellf_vitals purely because of GA4's 25-params-per-event
    // limit - vitals is already ~20 and 16 zone params would blow it. Dedupe on
    // GA4's own batch_page_id, which the duplicate-delivery analysis showed is stable
    // across the Instagram in-app browser's retries, so no extra id is sent. ──
    if (sent && zones.length) {
      try {
        zoneAccrueAll();
        // vw travels with the vector because the two layouts still differ even under
        // centre-band: the desktop gallery is excluded as sticky and the mobile one is not,
        // and the same section occupies a different share of a 390px and a 1512px screen.
        // Split on vw before comparing anything geometric. Verified 1 Sep, identical 6s
        // park on the plan cards: mobile plancards 6,536ms (49%, first), desktop plancards
        // 12,003ms (89%, first). Under the previous rule the same session put plancards
        // THIRD on mobile and the sticky gallery first on desktop.
        var zp = { page: PAGE, vw: window.innerWidth || 0 };
        for (var i = 0; i < zones.length; i++) {
          var z = zones[i];
          zp['z_' + z.name + '_ms'] = Math.round(z.ms);
          // _re only when it happened. With the socialproof zone this event hit exactly 25
          // params, which is GA4's ceiling: one more zone and parameters start dropping
          // silently. Re-entries are 0 for most zones on most pageviews, so sending them
          // sparsely takes the typical payload back to ~16 and leaves real headroom. An
          // absent z_*_re means zero, same as before.
          if (z.re) zp['z_' + z.name + '_re'] = z.re;
        }
        zp.plan_final = planTouched ? (planMode || 'none_default') : 'none_default';
        zp.toggle_count = planToggles;
        if (lastToggleAt >= 0) zp.last_toggle_to_exit_ms = Math.round(visibleMs - lastToggleAt);
        if (lastInputAt >= 0) zp.last_input_to_exit_ms = Math.round(visibleMs - lastInputAt);
        zp.exit_scroll_pct = maxScrollNow();
        zp.exit_zone = exitZoneName();
        if (typeof window.gtag === 'function') window.gtag('event', 'wellf_zone_dwell', zp);
        if (DEBUG) { try { console.log('[wellf-vitals] zone_dwell', zp); } catch (e) {} }
      } catch (e) {}
    }

    if (DEBUG) { try { console.log('[wellf-vitals]', sent ? 'sent' : 'NOT sent (no gtag)', p); } catch (e) {} }
  }

  document.addEventListener('visibilitychange', function () {
    accrue();                                    // bank the foreground chunk using the pre-change flag
    if (document.visibilityState === 'hidden') send();
    visible = (document.visibilityState !== 'hidden');
  });
  window.addEventListener('pagehide', send, { passive: true });

  if (DEBUG) { try { console.log('[wellf-vitals] armed on', PAGE); } catch (e) {} }
})();
